import moment from "moment";
import { useMemo } from "react";
import {
  milestoneValueDetail,
  resolveNextMilestone,
} from "../../utils/annual-planner-helpers";
import {
  getBodywweightAndUnit,
  getWaterIntakeAndUnit,
} from "../../utils/converters/convert-units";
import {
  buildGoalData,
  currentNutritionPhase,
  currentTrainingPhase,
  resolveActiveBodyweightGoal,
  resolveCustomGoals,
  resolveDailyTargetGoals,
  toUtcNumber,
} from "../../utils/nutrition-progress";
import { parseTime } from "../../views/athlete-portal/calendar/sleep-time/SleepTime";
import { goalStateLabel } from "../../views/athlete-portal/nutrition/components/views/BodyweightGoal";

// All the derived data behind PhaseSummary's 5 status chips (Training Phase,
// Nutrition Phase, Bodyweight Goal, Next Milestone, Daily Targets + Custom
// Goals), pulled out of AnnualPlanner.js so that component stays focused on
// the calendar/list rendering, and so each piece here can be memoized on its
// own real dependencies instead of recomputing on every AnnualPlanner render
// (toggling `listView`, selecting calendar days, etc. - none of which any of
// this data actually depends on).
export const usePhaseSummaryData = ({
  athlete,
  athleteStats,
  activeUser,
  clientGoals,
  t,
}) => {
  // Computed fresh each render, but a plain number - same value all render
  // long as it's still "today", so it never causes a memo below to recompute
  // needlessly.
  const todayUtc = toUtcNumber(new Date());

  const trainingPhase = useMemo(
    () => currentTrainingPhase(athlete, todayUtc),
    [athlete, todayUtc]
  );

  // Still reads as `null` for every athlete today - not because writes are
  // blocked (they aren't anymore), but because there is no UI yet to assign a
  // nutrition phase to a specific week (the checkbox grid is training-only,
  // see usePhaseSelection.js). Lights up for free once that ships.
  const nutritionPhase = useMemo(
    () => currentNutritionPhase(athlete, todayUtc),
    [athlete, todayUtc]
  );

  // Bodyweight goal status - same trend/projection engine as Nutrition
  // Progress's own goal card (buildGoalData/goalStateLabel), so the two
  // surfaces never disagree. `athleteStats` is this page's existing
  // (cheaper) stats source - see profile.controller.js's comment on why
  // Periodization skips the `dailyStats` load entirely.
  const activeBodyweightGoal = useMemo(
    () => resolveActiveBodyweightGoal(clientGoals, todayUtc),
    [clientGoals, todayUtc]
  );
  const bodyweightGoal = useMemo(() => {
    const goalData = buildGoalData(
      activeBodyweightGoal,
      athleteStats,
      todayUtc
    );
    if (!goalData) return null;
    const goalState =
      goalData.ratePerWeek != null ? goalStateLabel({ ...goalData, t }) : null;
    return {
      value: getBodywweightAndUnit(goalData.targetWeight, activeUser.units),
      status: goalState?.label ?? null,
      statusToken: goalState?.token ?? null,
    };
  }, [activeBodyweightGoal, athleteStats, todayUtc, activeUser.units, t]);

  // Daily targets (steps/sleep/water) - undated by convention (see the
  // "Info only — not dated" badge), so unlike trainingPhase/bodyweightGoal
  // there's no trend to compute, just "is a target set, and what is it".
  const dailyTargetGoals = useMemo(
    () => resolveDailyTargetGoals(clientGoals, todayUtc),
    [clientGoals, todayUtc]
  );
  const dailyTargets = useMemo(
    () =>
      [
        dailyTargetGoals.steps?.details?.targetSteps != null && {
          icon: "/img/new-icons/steps.svg",
          label: t("annualPlanner.dailyTargetSteps", {
            value: dailyTargetGoals.steps.details.targetSteps.toLocaleString(),
          }),
        },
        dailyTargetGoals.sleep?.details?.targetSleep != null && {
          icon: "/img/new-icons/sleep.svg",
          label: t("annualPlanner.dailyTargetSleep", {
            value: parseTime(dailyTargetGoals.sleep.details.targetSleep),
          }),
        },
        dailyTargetGoals.waterIntake?.details?.targetWaterIntake != null && {
          icon: "/img/new-icons/drop-icon.svg",
          label: t("annualPlanner.dailyTargetWater", {
            value: getWaterIntakeAndUnit(
              dailyTargetGoals.waterIntake.details.targetWaterIntake,
              activeUser.units
            ),
          }),
        },
      ].filter(Boolean),
    [dailyTargetGoals, activeUser.units, t]
  );

  // Custom goals (coach-defined name + free-text target + chosen icon) -
  // same "info only, not dated" convention as the daily targets above.
  const customGoals = useMemo(
    () =>
      resolveCustomGoals(clientGoals).map((goal) => ({
        icon: `/img/new-icons/${goal.details.customIcon}.svg`,
        label: `${goal.details.customName} - ${goal.details.customTarget}`,
      })),
    [clientGoals]
  );

  // Nearest upcoming dated goal (bodyWeight/personalRecord) or event - see
  // resolveNextMilestone's own comment for the PRD rule behind this merge.
  const events = athlete?.annualPlan?.events;
  const nextMilestoneData = useMemo(
    () => resolveNextMilestone(clientGoals, events, todayUtc),
    [clientGoals, events, todayUtc]
  );
  const nextMilestone = useMemo(() => {
    if (!nextMilestoneData) return null;
    const nextMilestoneLabels = {
      personalRecord: () =>
        t("annualPlanner.milestonePr", { name: nextMilestoneData.label }),
      bodyWeight: () => t("annualPlanner.milestoneBodyweightGoal"),
      event: () => nextMilestoneData.label,
    };
    // Kept separate from `label` (rather than one combined string) so
    // PhaseSummary can render it de-emphasized - smaller, lower opacity -
    // same treatment as the date. Shared with the Calendar grid's Milestone
    // row (milestoneValueDetail) so neither surface ever disagrees.
    const detail = milestoneValueDetail(nextMilestoneData, activeUser.units, t);
    return {
      label: nextMilestoneLabels[nextMilestoneData.kind](),
      detail,
      date: moment(String(nextMilestoneData.utcDate), "YYYYMMDD").format(
        "MMM D"
      ),
    };
  }, [nextMilestoneData, activeUser.units, t]);

  return {
    trainingPhase,
    nutritionPhase,
    bodyweightGoal,
    dailyTargets,
    customGoals,
    nextMilestone,
  };
};
